import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { Progress } from '../components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Award, Plus, Search, TrendingUp, CheckCircle, Clock, FileText,
  Target, BarChart3, Eye, Send, Loader2, Trash2, Copy, Building2,
  Layers, Settings, BookOpen, GraduationCap, Shield, Users, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import FacultyAppraisalTab from '../components/appraisal/FacultyAppraisalTab';

import { API_BASE as API_URL } from '../lib/apiBase';
const h = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } : {};
};

const STATUS_CFG = {
  draft:          { label: 'Draft',         color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  self_submitted: { label: 'Self Submitted', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  hod_reviewed:   { label: 'HOD Reviewed',   color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  finalized:      { label: 'Finalized',      color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
};
const GRADE_COLORS = { 'A+':'bg-emerald-500','A':'bg-emerald-400','B+':'bg-blue-500','B':'bg-blue-400','C':'bg-amber-500','D':'bg-red-500' };
const COMPLIANCE_ICONS = { AICTE: Shield, UGC: GraduationCap, CBSE: BookOpen, Custom: Settings };

// ========== TEMPLATE BUILDER TAB ==========
function TemplatesTab({ templates, onRefresh, entities, entityId }) {
  const [editTpl, setEditTpl] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const handleSeed = async () => {
    if (!entityId) { toast.error('Select an institute first'); return; }
    setSeeding(true);
    try {
      const res = await fetch(`${API_URL}/api/appraisal-templates/seed-defaults?entity_id=${entityId}`, { method: 'POST', headers: h() });
      const d = await res.json();
      toast.success(d.message);
      onRefresh();
    } catch { toast.error('Failed to seed'); }
    finally { setSeeding(false); }
  };

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/appraisal-templates`, { method: 'POST', headers: h() });
      if (!res.ok) throw new Error((await res.json()).detail);
      const tpl = await res.json();
      setEditTpl(tpl);
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await fetch(`${API_URL}/api/appraisal-templates/${id}`, { method: 'DELETE', headers: h() });
      toast.success('Deleted');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="templates-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Create custom appraisal forms based on compliance requirements</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSeed} disabled={seeding} data-testid="btn-seed-templates">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Layers className="w-4 h-4 mr-1" />}
            Seed AICTE/UGC/CBSE
          </Button>
          <Button onClick={handleCreate} data-testid="btn-create-template">
            <Plus className="w-4 h-4 mr-1" /> New Template
          </Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">
          <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No templates yet. Seed default templates or create a custom one.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(tpl => {
            const Icon = COMPLIANCE_ICONS[tpl.compliance_type] || Settings;
            return (
              <Card key={tpl.template_id} className="hover:border-slate-400 transition-colors cursor-pointer" data-testid={`tpl-card-${tpl.template_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#DC2626]" />
                      </div>
                      <Badge variant="outline" className="text-xs">{tpl.compliance_type}</Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditTpl(tpl)} data-testid={`btn-edit-${tpl.template_id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(tpl.template_id)} data-testid={`btn-del-${tpl.template_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">{tpl.name}</h3>
                  <p className="text-xs text-slate-500 mb-3 line-clamp-2">{tpl.description || 'No description'}</p>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>{tpl.sections?.length || 0} sections</span>
                    <span>{tpl.sections?.reduce((a, s) => a + (s.criteria?.length || 0), 0) || 0} criteria</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {editTpl && <TemplateEditorDialog tpl={editTpl} open={!!editTpl} onClose={() => setEditTpl(null)} onSaved={onRefresh} />}
    </div>
  );
}

// ========== TEMPLATE EDITOR DIALOG ==========
function TemplateEditorDialog({ tpl, open, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', compliance_type: 'Custom', description: '', sections: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tpl) setForm({ name: tpl.name || '', compliance_type: tpl.compliance_type || 'Custom', description: tpl.description || '', sections: tpl.sections || [] });
  }, [tpl]);

  const addSection = () => {
    setForm(f => ({ ...f, sections: [...f.sections, { section_id: `sec_${Date.now()}`, title: '', weightage: 0, criteria: [] }] }));
  };
  const removeSection = (idx) => setForm(f => ({ ...f, sections: f.sections.filter((_, i) => i !== idx) }));
  const updateSection = (idx, field, val) => setForm(f => {
    const s = [...f.sections]; s[idx] = { ...s[idx], [field]: val }; return { ...f, sections: s };
  });
  const addCriteria = (secIdx) => {
    setForm(f => {
      const s = [...f.sections];
      s[secIdx] = { ...s[secIdx], criteria: [...s[secIdx].criteria, { criteria_id: `crt_${Date.now()}`, name: '', description: '', scoring_type: 'rating_5', max_score: 5 }] };
      return { ...f, sections: s };
    });
  };
  const removeCriteria = (secIdx, critIdx) => {
    setForm(f => {
      const s = [...f.sections];
      s[secIdx] = { ...s[secIdx], criteria: s[secIdx].criteria.filter((_, i) => i !== critIdx) };
      return { ...f, sections: s };
    });
  };
  const updateCriteria = (secIdx, critIdx, field, val) => {
    setForm(f => {
      const s = [...f.sections];
      const c = [...s[secIdx].criteria];
      c[critIdx] = { ...c[critIdx], [field]: val };
      s[secIdx] = { ...s[secIdx], criteria: c };
      return { ...f, sections: s };
    });
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/appraisal-templates/${tpl.template_id}`, {
        method: 'PUT', headers: h(), body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Template saved');
      onSaved();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const totalWeightage = form.sections.reduce((a, s) => a + (parseFloat(s.weightage) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle>Edit Appraisal Template</DialogTitle>
          <DialogDescription>Customize sections and scoring criteria for this appraisal form.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Template Name *</Label>
              <Input data-testid="tpl-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label>Compliance Type</Label>
              <Select value={form.compliance_type} onValueChange={v => setForm(f => ({ ...f, compliance_type: v }))}>
                <SelectTrigger data-testid="tpl-compliance"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['AICTE', 'UGC', 'CBSE', 'Custom'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea data-testid="tpl-desc" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} />
          </div>

          {/* Sections */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-semibold">Sections</Label>
              <span className={`ml-2 text-xs ${totalWeightage === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                (Total weightage: {totalWeightage}%{totalWeightage !== 100 ? ' — should be 100%' : ' — perfect'})
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={addSection} data-testid="btn-add-section">
              <Plus className="w-3 h-3 mr-1" /> Add Section
            </Button>
          </div>

          {form.sections.map((sec, si) => (
            <Card key={sec.section_id} className="border-slate-200 dark:border-slate-700">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Input placeholder="Section title" value={sec.title} onChange={e => updateSection(si, 'title', e.target.value)} className="flex-1 font-medium" data-testid={`sec-title-${si}`} />
                  <div className="flex items-center gap-1 w-28">
                    <Input type="number" min="0" max="100" value={sec.weightage} onChange={e => updateSection(si, 'weightage', parseFloat(e.target.value) || 0)} className="w-16 text-center" data-testid={`sec-weight-${si}`} />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500 h-7 w-7 p-0" onClick={() => removeSection(si)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>

                {/* Criteria */}
                {sec.criteria.map((crit, ci) => (
                  <div key={crit.criteria_id} className="flex items-center gap-2 pl-4 border-l-2 border-slate-200 dark:border-slate-700">
                    <Input placeholder="Criteria name" value={crit.name} onChange={e => updateCriteria(si, ci, 'name', e.target.value)} className="flex-1 text-sm" />
                    <Select value={crit.scoring_type} onValueChange={v => {
                      const ms = v === 'rating_5' ? 5 : v === 'rating_10' ? 10 : v === 'numeric_100' ? 100 : v === 'yes_no' ? 1 : 0;
                      updateCriteria(si, ci, 'scoring_type', v);
                      updateCriteria(si, ci, 'max_score', ms);
                    }}>
                      <SelectTrigger className="w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rating_5">Rating (1-5)</SelectItem>
                        <SelectItem value="rating_10">Rating (1-10)</SelectItem>
                        <SelectItem value="numeric_100">Score (0-100)</SelectItem>
                        <SelectItem value="yes_no">Yes/No</SelectItem>
                        <SelectItem value="text">Text Only</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" className="text-red-400 h-6 w-6 p-0" onClick={() => removeCriteria(si, ci)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="text-xs ml-4" onClick={() => addCriteria(si)}>
                  <Plus className="w-3 h-3 mr-1" /> Add Criteria
                </Button>
              </CardContent>
            </Card>
          ))}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-template">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save Template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ========== CYCLES TAB ==========
function CyclesTab({ cycles, templates, employees, onRefresh, entityId }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null);

  return (
    <div className="space-y-4" data-testid="cycles-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Manage appraisal cycles and assign employees</p>
        <Button onClick={() => setDialogOpen(true)} data-testid="btn-create-cycle">
          <Plus className="w-4 h-4 mr-1" /> New Cycle
        </Button>
      </div>

      {cycles.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">
          <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No cycles yet. Create one to start appraisals.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {cycles.map(cyc => (
            <Card key={cyc.cycle_id} data-testid={`cycle-card-${cyc.cycle_id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900 dark:text-white">{cyc.name}</h3>
                    <Badge variant="outline" className="text-xs">{cyc.compliance_type}</Badge>
                    <Badge className={cyc.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>{cyc.status}</Badge>
                  </div>
                  <p className="text-xs text-slate-500">
                    Template: {cyc.template_name} | {cyc.start_date} to {cyc.end_date}
                  </p>
                </div>
                <Button size="sm" onClick={() => setAssignOpen(cyc)} data-testid={`btn-assign-${cyc.cycle_id}`}>
                  <Users className="w-4 h-4 mr-1" /> Assign Employees
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateCycleDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={onRefresh} templates={templates} entityId={entityId} />
      {assignOpen && <AssignEmployeesDialog cycle={assignOpen} employees={employees} open={!!assignOpen} onClose={() => setAssignOpen(null)} onDone={onRefresh} />}
    </div>
  );
}

function CreateCycleDialog({ open, onClose, onSaved, templates, entityId }) {
  const [form, setForm] = useState({ name: '', template_id: '', start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name || !form.template_id || !form.start_date || !form.end_date) { toast.error('All fields required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/appraisal/cycles`, {
        method: 'POST', headers: h(),
        body: JSON.stringify({ ...form, entity_id: entityId })
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Cycle created');
      onSaved(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="create-cycle-dialog">
        <DialogHeader>
          <DialogTitle>Create Appraisal Cycle</DialogTitle>
          <DialogDescription>Select a template and set the review period.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div><Label>Cycle Name *</Label><Input data-testid="cycle-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., 2025-26 Annual Review" /></div>
          <div>
            <Label>Appraisal Template *</Label>
            <Select value={form.template_id} onValueChange={v => setForm(f => ({ ...f, template_id: v }))}>
              <SelectTrigger data-testid="cycle-template"><SelectValue placeholder="Select template" /></SelectTrigger>
              <SelectContent>
                {templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name} ({t.compliance_type})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start Date *</Label><Input data-testid="cycle-start" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} /></div>
            <div><Label>End Date *</Label><Input data-testid="cycle-end" type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-cycle">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Create Cycle</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignEmployeesDialog({ cycle, employees, open, onClose, onDone }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = employees.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return `${e.first_name} ${e.last_name}`.toLowerCase().includes(s) || e.department?.toLowerCase().includes(s);
  });

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAll = () => setSelected(filtered.map(e => e.employee_id));

  const handleAssign = async () => {
    if (selected.length === 0) { toast.error('Select at least one employee'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/appraisals`, {
        method: 'POST', headers: h(),
        body: JSON.stringify({ cycle_id: cycle.cycle_id, employee_ids: selected })
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      const d = await res.json();
      toast.success(d.message);
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign Employees — {cycle.name}</DialogTitle>
          <DialogDescription>Select employees to create appraisals for this cycle.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="flex gap-2">
            <Input placeholder="Search employees..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1" />
            <Button variant="outline" size="sm" onClick={selectAll}>Select All ({filtered.length})</Button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2">
            {filtered.map(emp => (
              <label key={emp.employee_id} className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 ${selected.includes(emp.employee_id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                <input type="checkbox" checked={selected.includes(emp.employee_id)} onChange={() => toggle(emp.employee_id)} className="rounded" />
                <div>
                  <p className="text-sm font-medium">{emp.first_name} {emp.last_name}</p>
                  <p className="text-xs text-slate-500">{emp.department} — {emp.designation}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500">{selected.length} selected</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleAssign} disabled={saving} data-testid="btn-confirm-assign">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Create Appraisals ({selected.length})
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ========== APPRAISAL FORM DIALOG (Self/HOD) ==========
function AppraisalFormDialog({ appraisal, mode, open, onClose, onDone }) {
  // mode: 'self' | 'hod' | 'view'
  const [sections, setSections] = useState([]);
  const [achievements, setAchievements] = useState('');
  const [improvements, setImprovements] = useState('');
  const [overallComment, setOverallComment] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (appraisal) {
      setSections(JSON.parse(JSON.stringify(appraisal.sections || [])));
      setAchievements(appraisal.self_achievements || '');
      setImprovements(appraisal.self_improvements || '');
      setOverallComment(appraisal.hod_overall_comment || '');
    }
  }, [appraisal]);

  const updateScore = (secIdx, critIdx, field, val) => {
    setSections(ss => {
      const s = JSON.parse(JSON.stringify(ss));
      s[secIdx].criteria[critIdx][field] = val;
      return s;
    });
  };

  const renderScoreInput = (crit, secIdx, critIdx, scoreField, commentField) => {
    const isView = mode === 'view';
    const st = crit.scoring_type;
    const val = crit[scoreField];
    const comment = crit[commentField] || '';

    if (st === 'text') {
      return <Textarea value={comment} onChange={e => updateScore(secIdx, critIdx, commentField, e.target.value)} disabled={isView} placeholder="Enter text..." rows={2} className="text-sm" />;
    }
    if (st === 'yes_no') {
      return (
        <div className="flex items-center gap-4">
          <Select value={val != null ? String(val) : ''} onValueChange={v => updateScore(secIdx, critIdx, scoreField, parseFloat(v))} disabled={isView}>
            <SelectTrigger className="w-24"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent><SelectItem value="1">Yes</SelectItem><SelectItem value="0">No</SelectItem></SelectContent>
          </Select>
          <Input placeholder="Comment" value={comment} onChange={e => updateScore(secIdx, critIdx, commentField, e.target.value)} disabled={isView} className="flex-1 text-sm" />
        </div>
      );
    }
    const max = crit.max_score || (st === 'rating_5' ? 5 : st === 'rating_10' ? 10 : 100);
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <Slider value={[val || 0]} onValueChange={([v]) => updateScore(secIdx, critIdx, scoreField, v)} max={max} step={1} className="flex-1" disabled={isView} />
          <span className="font-bold text-sm w-14 text-right">{val || 0}/{max}</span>
        </div>
        <Input placeholder="Comment (optional)" value={comment} onChange={e => updateScore(secIdx, critIdx, commentField, e.target.value)} disabled={isView} className="text-xs" />
      </div>
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const endpoint = mode === 'self' ? 'self-submit' : 'hod-review';
      const body = { sections };
      if (mode === 'self') { body.achievements = achievements; body.improvements = improvements; }
      if (mode === 'hod') { body.overall_comment = overallComment; }
      const res = await fetch(`${API_URL}/api/appraisals/${appraisal.appraisal_id}/${endpoint}`, {
        method: 'PUT', headers: h(), body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      const d = await res.json();
      toast.success(d.message);
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (!appraisal) return null;
  const scoreField = mode === 'hod' ? 'hod_score' : 'self_score';
  const commentField = mode === 'hod' ? 'hod_comment' : 'self_comment';
  const isView = mode === 'view';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="appraisal-form-dialog">
        <DialogHeader>
          <DialogTitle>{isView ? 'View' : mode === 'self' ? 'Self Assessment' : 'HOD Review'} — {appraisal.employee_name}</DialogTitle>
          <DialogDescription>{appraisal.template_name} ({appraisal.compliance_type}) | {appraisal.cycle_name}</DialogDescription>
        </DialogHeader>

        {/* Scores summary if viewing */}
        {isView && (
          <div className="grid grid-cols-3 gap-3 mt-2">
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-center">
              <p className="text-xs text-slate-500">Self Score</p>
              <p className="text-lg font-bold text-blue-700">{appraisal.self_total_score?.toFixed(1) || '—'}%</p>
            </div>
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-center">
              <p className="text-xs text-slate-500">HOD Score</p>
              <p className="text-lg font-bold text-amber-700">{appraisal.hod_total_score?.toFixed(1) || '—'}%</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-center">
              <p className="text-xs text-slate-500">Final Score</p>
              <p className="text-lg font-bold text-emerald-700">{appraisal.final_score?.toFixed(1) || '—'}% {appraisal.final_grade && `(${appraisal.final_grade})`}</p>
            </div>
          </div>
        )}

        {/* Attendance Metrics (auto-populated from attendance data) */}
        {isView && appraisal.attendance_metrics && (
          <div className="mt-3 p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100" data-testid="appraisal-attendance-metrics">
            <p className="text-xs font-semibold text-indigo-700 mb-2 flex items-center gap-1">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M2 12h20"/><circle cx="12" cy="12" r="10"/></svg>
              Attendance Analytics (Auto-populated)
            </p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <p className="text-sm font-bold text-indigo-700">{appraisal.attendance_metrics.punctuality_score}%</p>
                <p className="text-xs text-slate-500">Punctuality</p>
              </div>
              <div>
                <p className="text-sm font-bold text-indigo-700">{appraisal.attendance_metrics.attendance_percentage}%</p>
                <p className="text-xs text-slate-500">Attendance</p>
              </div>
              <div>
                <p className="text-sm font-bold text-indigo-700">{appraisal.attendance_metrics.late_count}</p>
                <p className="text-xs text-slate-500">Late Days</p>
              </div>
              <div>
                <p className="text-sm font-bold text-indigo-700">{appraisal.attendance_metrics.avg_work_hours}h</p>
                <p className="text-xs text-slate-500">Avg Hours</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6 mt-4">
          {sections.map((sec, si) => (
            <div key={sec.section_id}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-800 dark:text-white">{sec.title}</h3>
                <Badge variant="outline" className="text-xs">{sec.weightage}% weightage</Badge>
              </div>
              <div className="space-y-4">
                {sec.criteria.map((crit, ci) => (
                  <div key={crit.criteria_id} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium">{crit.name}</p>
                        {crit.description && <p className="text-xs text-slate-500">{crit.description}</p>}
                      </div>
                      <Badge variant="outline" className="text-xs">{crit.scoring_type?.replace('_', ' ')}</Badge>
                    </div>
                    {/* Show self scores in HOD/view mode */}
                    {(mode === 'hod' || isView) && crit.self_score != null && (
                      <div className="text-xs text-blue-600 mb-2">Employee self-score: {crit.self_score}/{crit.max_score} {crit.self_comment && `— "${crit.self_comment}"`}</div>
                    )}
                    {/* Show HOD scores in view mode */}
                    {isView && crit.hod_score != null && (
                      <div className="text-xs text-amber-600 mb-2">HOD score: {crit.hod_score}/{crit.max_score} {crit.hod_comment && `— "${crit.hod_comment}"`}</div>
                    )}
                    {/* Score input */}
                    {!isView && renderScoreInput(crit, si, ci, scoreField, commentField)}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Self extras */}
          {mode === 'self' && (
            <div className="space-y-3 border-t pt-4">
              <div><Label>Key Achievements</Label><Textarea value={achievements} onChange={e => setAchievements(e.target.value)} rows={2} placeholder="Your achievements this period..." /></div>
              <div><Label>Areas for Improvement</Label><Textarea value={improvements} onChange={e => setImprovements(e.target.value)} rows={2} placeholder="Areas you want to improve..." /></div>
            </div>
          )}
          {mode === 'hod' && (
            <div className="border-t pt-4">
              <Label>Overall Comment</Label>
              <Textarea value={overallComment} onChange={e => setOverallComment(e.target.value)} rows={2} placeholder="Your overall assessment..." />
            </div>
          )}

          {!isView && (
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={saving} data-testid="btn-submit-appraisal">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                {mode === 'self' ? 'Submit Self Assessment' : 'Submit HOD Review'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ========== HR FINALIZE DIALOG ==========
function FinalizeDialog({ appraisal, open, onClose, onDone }) {
  const [comment, setComment] = useState('');
  const [recommendation, setRecommendation] = useState('approve');
  const [gradeOverride, setGradeOverride] = useState('');
  const [saving, setSaving] = useState(false);

  const handleFinalize = async () => {
    setSaving(true);
    try {
      const body = { comment, recommendation };
      if (gradeOverride) body.final_grade_override = gradeOverride;
      const res = await fetch(`${API_URL}/api/appraisals/${appraisal.appraisal_id}/hr-finalize`, {
        method: 'PUT', headers: h(), body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Appraisal finalized');
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (!appraisal) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="finalize-dialog">
        <DialogHeader>
          <DialogTitle>Finalize Appraisal</DialogTitle>
          <DialogDescription>{appraisal.employee_name} — Score: {appraisal.final_score?.toFixed(1)}% ({appraisal.final_grade})</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div><Label>Comment</Label><Textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} /></div>
          <div>
            <Label>Recommendation</Label>
            <Select value={recommendation} onValueChange={setRecommendation}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="promote">Recommend Promotion</SelectItem>
                <SelectItem value="training">Recommend Training</SelectItem>
                <SelectItem value="pip">Performance Improvement Plan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Override Grade (optional)</Label>
            <Select value={gradeOverride} onValueChange={setGradeOverride}>
              <SelectTrigger><SelectValue placeholder="Keep current grade" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">Keep current</SelectItem>
                {Object.keys(GRADE_COLORS).map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleFinalize} disabled={saving} data-testid="btn-confirm-finalize">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />} Finalize
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ========== MAIN COMPONENT ==========
export default function Performance() {
  const { selectedEntity, entities } = useOutletContext() || {};
  const { user } = useAuth();
  const role = user?.role || 'employee';
  const isHR = ['hr', 'admin', 'super_admin'].includes(role);
  const isHOD = role === 'hod' || role === 'super_admin';
  const isSuperAdmin = role === 'super_admin';
  const isEmployee = role === 'employee';

  const [templates, setTemplates] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [crossInstitute, setCrossInstitute] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialog states
  const [formDialog, setFormDialog] = useState({ open: false, appraisal: null, mode: 'view' });
  const [finalizeDialog, setFinalizeDialog] = useState({ open: false, appraisal: null });

  const entityId = selectedEntity?.entity_id || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const ep = entityId ? `?entity_id=${entityId}` : '';
      const calls = [
        fetch(`${API_URL}/api/appraisals${ep}`, { headers: h() }),
      ];
      if (isHR) {
        calls.push(fetch(`${API_URL}/api/appraisal-templates${ep}`, { headers: h() }));
        calls.push(fetch(`${API_URL}/api/appraisal/cycles${ep}`, { headers: h() }));
        calls.push(fetch(`${API_URL}/api/employees${ep}`, { headers: h() }));
      }
      if (isSuperAdmin) {
        calls.push(fetch(`${API_URL}/api/appraisals/cross-institute-summary`, { headers: h() }));
      }
      const results = await Promise.all(calls);
      const [apprRes, tplRes, cycRes, empRes, ciRes] = results;
      if (apprRes?.ok) setAppraisals(await apprRes.json());
      if (tplRes?.ok) setTemplates(await tplRes.json());
      if (cycRes?.ok) setCycles(await cycRes.json());
      if (empRes?.ok) setEmployees(await empRes.json());
      if (ciRes?.ok) setCrossInstitute(await ciRes.json());
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [entityId, isHR, isSuperAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = appraisals.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return a.employee_name?.toLowerCase().includes(s) || a.department?.toLowerCase().includes(s) || a.template_name?.toLowerCase().includes(s);
    }
    return true;
  });

  const stats = {
    total: appraisals.length,
    draft: appraisals.filter(a => a.status === 'draft').length,
    self_submitted: appraisals.filter(a => a.status === 'self_submitted').length,
    hod_reviewed: appraisals.filter(a => a.status === 'hod_reviewed').length,
    finalized: appraisals.filter(a => a.status === 'finalized').length,
  };

  // Fetch full appraisal detail (includes attendance_metrics)
  const openAppraisalDialog = async (appraisalId, mode) => {
    try {
      const res = await fetch(`${API_URL}/api/appraisals/${appraisalId}`, { headers: h() });
      if (res.ok) {
        const fullAppraisal = await res.json();
        setFormDialog({ open: true, appraisal: fullAppraisal, mode });
      } else {
        toast.error('Failed to load appraisal details');
      }
    } catch (e) {
      toast.error('Failed to load appraisal');
    }
  };

  const getActions = (a) => {
    const actions = [];
    actions.push({ label: 'View', onClick: () => openAppraisalDialog(a.appraisal_id, 'view'), variant: 'ghost' });
    if (isEmployee && a.status === 'draft') {
      actions.push({ label: 'Self Assess', onClick: () => openAppraisalDialog(a.appraisal_id, 'self'), variant: 'default' });
    }
    if (isHOD && a.status === 'self_submitted') {
      actions.push({ label: 'HOD Review', onClick: () => openAppraisalDialog(a.appraisal_id, 'hod'), variant: 'default' });
    }
    if (isHR && a.status === 'hod_reviewed') {
      actions.push({ label: 'Finalize', onClick: () => setFinalizeDialog({ open: true, appraisal: a }), variant: 'default' });
    }
    return actions;
  };

  const defaultTab = 'faculty-appraisal';

  return (
    <div className="space-y-6" data-testid="performance-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Performance Appraisal</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isHR ? 'Create custom appraisal forms, manage cycles, and track employee performance' :
           isEmployee ? 'View and submit your performance self-assessment' :
           'Review and manage employee performance evaluations'}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="appraisal-stats">
        {[
          { label: 'Total', value: stats.total, icon: Award, color: 'text-slate-600' },
          { label: 'Draft', value: stats.draft, icon: FileText, color: 'text-slate-500' },
          { label: 'Self Submitted', value: stats.self_submitted, icon: Send, color: 'text-blue-600' },
          { label: 'HOD Reviewed', value: stats.hod_reviewed, icon: Clock, color: 'text-amber-600' },
          { label: 'Finalized', value: stats.finalized, icon: CheckCircle, color: 'text-emerald-600' },
        ].map(c => (
          <Card key={c.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <c.icon className={`w-5 h-5 ${c.color}`} />
              <div>
                <p className="text-2xl font-bold">{c.value}</p>
                <p className="text-xs text-slate-500">{c.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue={defaultTab}>
        <TabsList>
          <TabsTrigger value="faculty-appraisal" data-testid="tab-faculty-appraisal">Faculty Appraisal (SGI)</TabsTrigger>
          {isHR && <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>}
          {isHR && <TabsTrigger value="cycles" data-testid="tab-cycles">Cycles</TabsTrigger>}
          <TabsTrigger value="appraisals" data-testid="tab-appraisals">{isEmployee ? 'My Appraisals' : 'All Appraisals'}</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="overview" data-testid="tab-overview">Cross-Institute</TabsTrigger>}
        </TabsList>

        <TabsContent value="faculty-appraisal">
          <FacultyAppraisalTab entityId={entityId} />
        </TabsContent>

        {isHR && (
          <TabsContent value="templates">
            <TemplatesTab templates={templates} onRefresh={fetchData} entities={entities || []} entityId={entityId} />
          </TabsContent>
        )}
        {isHR && (
          <TabsContent value="cycles">
            <CyclesTab cycles={cycles} templates={templates} employees={employees} onRefresh={fetchData} entityId={entityId} />
          </TabsContent>
        )}

        <TabsContent value="appraisals">
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input className="pl-9" placeholder="Search by name, department, template..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-appraisals" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.entries(STATUS_CFG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : filtered.length === 0 ? (
                  <div className="py-16 text-center text-slate-400">
                    <Award className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">{isEmployee ? 'No appraisals assigned yet' : 'No appraisals found'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead>Template</TableHead>
                          <TableHead>Cycle</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Self Score</TableHead>
                          <TableHead>Final</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map(a => (
                          <TableRow key={a.appraisal_id} data-testid={`appr-row-${a.appraisal_id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white">{a.employee_name}</p>
                                <p className="text-xs text-slate-500">{a.department}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{a.compliance_type}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">{a.cycle_name}</TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CFG[a.status]?.color || ''}`}>
                                {STATUS_CFG[a.status]?.label || a.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">{a.self_total_score > 0 ? `${a.self_total_score.toFixed(1)}%` : '—'}</TableCell>
                            <TableCell>
                              {a.final_score > 0 ? (
                                <div className="flex items-center gap-2">
                                  <span className="font-bold">{a.final_score.toFixed(1)}%</span>
                                  {a.final_grade && <Badge className={`${GRADE_COLORS[a.final_grade] || 'bg-slate-500'} text-white text-xs`}>{a.final_grade}</Badge>}
                                </div>
                              ) : '—'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {getActions(a).map((act, i) => (
                                  <Button key={i} variant={act.variant} size="sm" onClick={act.onClick} className="text-xs" data-testid={`btn-${act.label.toLowerCase().replace(/\s/g, '-')}-${a.appraisal_id}`}>
                                    {act.label}
                                  </Button>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="overview">
            <div className="space-y-4" data-testid="cross-institute-tab">
              <p className="text-sm text-slate-500">Appraisal progress across all institutes</p>
              {crossInstitute.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-slate-400">No data yet</CardContent></Card>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {crossInstitute.map(ci => (
                    <Card key={ci.entity_id} data-testid={`ci-card-${ci.entity_id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <Building2 className="w-5 h-5 text-[#DC2626]" />
                          <h3 className="font-semibold text-slate-900 dark:text-white">{ci.entity_name}</h3>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center text-sm">
                          <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                            <p className="text-lg font-bold">{ci.total_appraisals}</p>
                            <p className="text-xs text-slate-500">Total</p>
                          </div>
                          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                            <p className="text-lg font-bold text-blue-600">{ci.self_submitted + ci.hod_reviewed}</p>
                            <p className="text-xs text-slate-500">In Review</p>
                          </div>
                          <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded">
                            <p className="text-lg font-bold text-emerald-600">{ci.finalized}</p>
                            <p className="text-xs text-slate-500">Finalized</p>
                          </div>
                        </div>
                        {ci.total_appraisals > 0 && (
                          <div className="mt-3">
                            <Progress value={(ci.finalized / ci.total_appraisals) * 100} className="h-2" />
                            <p className="text-xs text-slate-500 mt-1">{((ci.finalized / ci.total_appraisals) * 100).toFixed(0)}% complete</p>
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-2">{ci.templates_count} templates configured</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Dialogs */}
      <AppraisalFormDialog
        appraisal={formDialog.appraisal}
        mode={formDialog.mode}
        open={formDialog.open}
        onClose={() => setFormDialog({ open: false, appraisal: null, mode: 'view' })}
        onDone={fetchData}
      />
      <FinalizeDialog
        appraisal={finalizeDialog.appraisal}
        open={finalizeDialog.open}
        onClose={() => setFinalizeDialog({ open: false, appraisal: null })}
        onDone={fetchData}
      />
    </div>
  );
}
